import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card } from '../../components/ui/card';
import { resolveFileUrl } from '../../utils/fileUrl';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { Users, Search, CheckCircle, XCircle, Clock, Eye, Trash2, KeyRound, Edit, X, Save, FileText, Image, Download } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const MemberManagement = () => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedMember, setSelectedMember] = useState(null);
  const [resetPasswordModal, setResetPasswordModal] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [resettingPassword, setResettingPassword] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchMembers();
  }, []);

  const fetchMembers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/members`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMembers(response.data);
    } catch (error) {
      toast.error('Failed to fetch members');
    } finally {
      setLoading(false);
    }
  };

  const updateMemberStatus = async (memberId, status) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API}/members/${memberId}/status`, { status }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Member ${status === 'approved' ? 'approved' : 'rejected'} successfully`);
      fetchMembers();
      setSelectedMember(null);
    } catch (error) {
      console.error('Update status error:', error.response?.data || error.message);
      toast.error(error.response?.data?.detail || 'Failed to update member status');
    }
  };

  const deleteMember = async (memberId) => {
    if (!window.confirm('Are you sure you want to delete this member?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/members/${memberId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Member deleted successfully');
      fetchMembers();
    } catch (error) {
      toast.error('Failed to delete member');
    }
  };

  const resetPassword = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setResettingPassword(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/admin/reset-password`, {
        email: resetPasswordModal.email,
        userType: 'member',
        newPassword: newPassword
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Password reset successfully for ${resetPasswordModal.fullName}`);
      setResetPasswordModal(null);
      setNewPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setResettingPassword(false);
    }
  };

  const openEditModal = (member) => {
    setEditingMember(member);
    setEditFormData({
      fullName: member.fullName || '',
      fatherOrSpouseName: member.fatherOrSpouseName || '',
      email: member.email || '',
      phone: member.phone || '',
      whatsapp: member.whatsapp || '',
      dateOfBirth: member.dateOfBirth || '',
      gender: member.gender || '',
      bloodGroup: member.bloodGroup || '',
      maritalStatus: member.maritalStatus || '',
      gotra: member.gotra || '',
      nukh: member.nukh || '',
      familyName: member.familyName || '',
      country: member.country || '',
      state: member.state || '',
      city: member.city || '',
      pincode: member.pincode || '',
      address: member.address || '',
      education: member.education || member.educationalQualification || '',
      occupation: member.occupation || '',
      organization: member.organization || '',
      designation: member.designation || '',
      bio: member.bio || '',
      status: member.status || 'pending'
    });
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({ ...prev, [name]: value }));
  };

  const saveMemberEdit = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/members/${editingMember.id}`, editFormData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Member updated successfully');
      setEditingMember(null);
      fetchMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update member');
    } finally {
      setSaving(false);
    }
  };

  const filteredMembers = members.filter(member => {
    const matchesSearch = member.fullName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         member.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         member.phone?.includes(searchTerm);
    const matchesStatus = filterStatus === 'all' || member.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
    };
    const icons = {
      pending: <Clock className="w-3 h-3" />,
      approved: <CheckCircle className="w-3 h-3" />,
      rejected: <XCircle className="w-3 h-3" />,
    };
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${styles[status] || styles.pending}`}>
        {icons[status]} {status}
      </span>
    );
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-serif font-bold text-3xl text-primary mb-2">Member Management</h1>
          <p className="text-muted-foreground">View and manage member registrations</p>
        </div>
        <div className="flex items-center gap-2">
          <Users className="w-5 h-5 text-muted-foreground" />
          <span className="text-lg font-semibold">{members.length} Total Members</span>
        </div>
      </div>

      {/* Filters */}
      <Card className="p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search by name, email, or phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <div className="flex gap-2">
            {['all', 'pending', 'approved', 'rejected'].map((status) => (
              <Button
                key={status}
                variant={filterStatus === status ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilterStatus(status)}
                className="capitalize"
              >
                {status} ({status === 'all' ? members.length : members.filter(m => m.status === status).length})
              </Button>
            ))}
          </div>
        </div>
      </Card>

      {/* Members Table */}
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold">Member</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Contact</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Membership ID</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Status</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Joined</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {loading ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">
                    Loading members...
                  </td>
                </tr>
              ) : filteredMembers.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">
                    No members found
                  </td>
                </tr>
              ) : (
                filteredMembers.map((member) => (
                  <tr key={member.id} className="hover:bg-muted/50">
                    <td className="px-4 py-3">
                      <div>
                        <p className="font-semibold text-foreground">{member.fullName}</p>
                        <p className="text-sm text-muted-foreground">{member.fatherOrSpouseName}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div>
                        <p className="text-sm">{member.email}</p>
                        <p className="text-sm text-muted-foreground">{member.phone}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="font-mono text-sm">{member.membershipId}</span>
                    </td>
                    <td className="px-4 py-3">
                      {getStatusBadge(member.status)}
                    </td>
                    <td className="px-4 py-3 text-sm text-muted-foreground">
                      {new Date(member.joinedDate).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setSelectedMember(member)}
                          title="View Details"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-orange-600 hover:text-orange-700"
                          onClick={() => openEditModal(member)}
                          title="Edit Member"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-blue-600 hover:text-blue-700"
                          onClick={() => setResetPasswordModal(member)}
                          title="Reset Password"
                        >
                          <KeyRound className="w-4 h-4" />
                        </Button>
                        {member.status === 'pending' && (
                          <>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-green-600 hover:text-green-700"
                              onClick={() => updateMemberStatus(member.id, 'approved')}
                              title="Approve"
                            >
                              <CheckCircle className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-600 hover:text-red-700"
                              onClick={() => updateMemberStatus(member.id, 'rejected')}
                              title="Reject"
                            >
                              <XCircle className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:text-red-700"
                          onClick={() => deleteMember(member.id)}
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Member Detail Modal */}
      {selectedMember && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setSelectedMember(null)}>
          <Card className="max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="p-6">
              <div className="flex justify-between items-start mb-6">
                <div className="flex items-start gap-4">
                  {/* Profile Photo */}
                  <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center overflow-hidden border-2 border-primary/20">
                    {selectedMember.profilePhoto ? (
                      <img 
                        src={resolveFileUrl(selectedMember.profilePhoto)} 
                        alt={selectedMember.fullName} 
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <Users className="w-10 h-10 text-primary/50" />
                    )}
                  </div>
                  <div>
                    <h2 className="font-serif font-bold text-2xl text-primary">{selectedMember.fullName}</h2>
                    <p className="text-muted-foreground">{selectedMember.membershipId}</p>
                  </div>
                </div>
                {getStatusBadge(selectedMember.status)}
              </div>

              {/* Documents Section */}
              {selectedMember.profilePhoto && (
                <div className="mb-6 p-4 bg-muted/50 rounded-lg">
                  <h3 className="font-semibold text-sm text-muted-foreground mb-3 flex items-center gap-2">
                    <FileText className="w-4 h-4" /> Uploaded Documents
                  </h3>
                  <div className="flex flex-wrap gap-3">
                    <a
                      href={resolveFileUrl(selectedMember.profilePhoto)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-3 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors text-sm"
                      data-testid="view-profile-photo"
                    >
                      <Image className="w-4 h-4" />
                      View Profile Photo
                    </a>
                    <a
                      href={resolveFileUrl(selectedMember.profilePhoto)}
                      download
                      className="inline-flex items-center gap-2 px-3 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors text-sm"
                      data-testid="download-profile-photo"
                    >
                      <Download className="w-4 h-4" />
                      Download
                    </a>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4 mb-6">
                <div>
                  <p className="text-sm text-muted-foreground">Father/Spouse Name</p>
                  <p className="font-medium">{selectedMember.fatherOrSpouseName || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Date of Birth</p>
                  <p className="font-medium">{selectedMember.dateOfBirth || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Email</p>
                  <p className="font-medium">{selectedMember.email}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Phone</p>
                  <p className="font-medium">{selectedMember.phone}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Alternate Contact</p>
                  <p className="font-medium">{selectedMember.alternateContact || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Education</p>
                  <p className="font-medium">{selectedMember.educationalQualification || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Occupation</p>
                  <p className="font-medium">{selectedMember.occupation || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Designation</p>
                  <p className="font-medium">{selectedMember.designation || '-'}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-sm text-muted-foreground">Address</p>
                  <p className="font-medium">{selectedMember.address}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-sm text-muted-foreground">Correspondence Address</p>
                  <p className="font-medium">{selectedMember.correspondenceAddress || '-'}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-sm text-muted-foreground">Workplace Address</p>
                  <p className="font-medium">{selectedMember.workplaceAddress || '-'}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-sm text-muted-foreground">Social Involvement</p>
                  <p className="font-medium">{selectedMember.socialInvolvement || '-'}</p>
                </div>
              </div>

              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  className="text-blue-600"
                  onClick={() => {
                    setSelectedMember(null);
                    setResetPasswordModal(selectedMember);
                  }}
                >
                  <KeyRound className="w-4 h-4 mr-2" /> Reset Password
                </Button>
                {selectedMember.status === 'pending' && (
                  <>
                    <Button
                      variant="outline"
                      className="text-red-600"
                      onClick={() => updateMemberStatus(selectedMember.id, 'rejected')}
                    >
                      <XCircle className="w-4 h-4 mr-2" /> Reject
                    </Button>
                    <Button
                      className="bg-green-600 hover:bg-green-700"
                      onClick={() => updateMemberStatus(selectedMember.id, 'approved')}
                    >
                      <CheckCircle className="w-4 h-4 mr-2" /> Approve
                    </Button>
                  </>
                )}
                <Button variant="outline" onClick={() => setSelectedMember(null)}>
                  Close
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Reset Password Modal */}
      {resetPasswordModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setResetPasswordModal(null)}>
          <Card className="max-w-md w-full" onClick={e => e.stopPropagation()}>
            <div className="p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                  <KeyRound className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h2 className="font-bold text-xl text-gray-900">Reset Password</h2>
                  <p className="text-sm text-gray-500">पासवर्ड रीसेट करें</p>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <p className="text-sm text-gray-600 mb-1">Resetting password for:</p>
                <p className="font-semibold text-gray-900">{resetPasswordModal.fullName}</p>
                <p className="text-sm text-gray-500">{resetPasswordModal.email}</p>
              </div>

              <form onSubmit={resetPassword} className="space-y-4">
                <div>
                  <Label htmlFor="newPassword">New Password / नया पासवर्ड</Label>
                  <Input
                    id="newPassword"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter new password (min 6 characters)"
                    required
                    minLength={6}
                  />
                </div>

                <div className="flex gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    className="flex-1"
                    onClick={() => {
                      setResetPasswordModal(null);
                      setNewPassword('');
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    className="flex-1 bg-blue-600 hover:bg-blue-700"
                    disabled={resettingPassword}
                  >
                    {resettingPassword ? 'Resetting...' : 'Reset Password'}
                  </Button>
                </div>
              </form>
            </div>
          </Card>
        </div>
      )}

      {/* Edit Member Modal */}
      {editingMember && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setEditingMember(null)}>
          <Card className="max-w-4xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                    <Edit className="w-6 h-6 text-orange-600" />
                  </div>
                  <div>
                    <h2 className="font-bold text-xl text-gray-900">Edit Member</h2>
                    <p className="text-sm text-gray-500">सदस्य संपादित करें</p>
                  </div>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setEditingMember(null)}>
                  <X className="w-5 h-5" />
                </Button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <Label>Full Name / पूरा नाम</Label>
                  <Input name="fullName" value={editFormData.fullName} onChange={handleEditChange} />
                </div>
                <div>
                  <Label>Father/Spouse Name / पिता/पति का नाम</Label>
                  <Input name="fatherOrSpouseName" value={editFormData.fatherOrSpouseName} onChange={handleEditChange} />
                </div>
                <div>
                  <Label>Email / ईमेल</Label>
                  <Input name="email" type="email" value={editFormData.email} onChange={handleEditChange} />
                </div>
                <div>
                  <Label>Phone / फ़ोन</Label>
                  <Input name="phone" value={editFormData.phone} onChange={handleEditChange} />
                </div>
                <div>
                  <Label>WhatsApp / व्हाट्सएप</Label>
                  <Input name="whatsapp" value={editFormData.whatsapp} onChange={handleEditChange} />
                </div>
                <div>
                  <Label>Date of Birth / जन्म तिथि</Label>
                  <Input name="dateOfBirth" type="date" value={editFormData.dateOfBirth} onChange={handleEditChange} />
                </div>
                <div>
                  <Label>Gender / लिंग</Label>
                  <select name="gender" value={editFormData.gender} onChange={handleEditChange} className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm">
                    <option value="">Select</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div>
                  <Label>Blood Group / रक्त समूह</Label>
                  <Input name="bloodGroup" value={editFormData.bloodGroup} onChange={handleEditChange} />
                </div>
                <div>
                  <Label>Marital Status / वैवाहिक स्थिति</Label>
                  <select name="maritalStatus" value={editFormData.maritalStatus} onChange={handleEditChange} className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm">
                    <option value="">Select</option>
                    <option value="Single">Single</option>
                    <option value="Married">Married</option>
                    <option value="Widowed">Widowed</option>
                    <option value="Divorced">Divorced</option>
                  </select>
                </div>
                <div>
                  <Label>Gotra / गोत्र</Label>
                  <Input name="gotra" value={editFormData.gotra} onChange={handleEditChange} />
                </div>
                <div>
                  <Label>Nukh / नुख</Label>
                  <Input name="nukh" value={editFormData.nukh} onChange={handleEditChange} />
                </div>
                <div>
                  <Label>Family Name / परिवार का नाम</Label>
                  <Input name="familyName" value={editFormData.familyName} onChange={handleEditChange} />
                </div>
                <div>
                  <Label>Country / देश</Label>
                  <Input name="country" value={editFormData.country} onChange={handleEditChange} />
                </div>
                <div>
                  <Label>State / राज्य</Label>
                  <Input name="state" value={editFormData.state} onChange={handleEditChange} />
                </div>
                <div>
                  <Label>City / शहर</Label>
                  <Input name="city" value={editFormData.city} onChange={handleEditChange} />
                </div>
                <div>
                  <Label>Pincode / पिनकोड</Label>
                  <Input name="pincode" value={editFormData.pincode} onChange={handleEditChange} />
                </div>
                <div className="md:col-span-2">
                  <Label>Address / पता</Label>
                  <Input name="address" value={editFormData.address} onChange={handleEditChange} />
                </div>
                <div>
                  <Label>Education / शिक्षा</Label>
                  <Input name="education" value={editFormData.education} onChange={handleEditChange} />
                </div>
                <div>
                  <Label>Occupation / व्यवसाय</Label>
                  <Input name="occupation" value={editFormData.occupation} onChange={handleEditChange} />
                </div>
                <div>
                  <Label>Organization / संस्था</Label>
                  <Input name="organization" value={editFormData.organization} onChange={handleEditChange} />
                </div>
                <div>
                  <Label>Designation / पद</Label>
                  <Input name="designation" value={editFormData.designation} onChange={handleEditChange} />
                </div>
                <div>
                  <Label>Status / स्थिति</Label>
                  <select name="status" value={editFormData.status} onChange={handleEditChange} className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm">
                    <option value="pending">Pending</option>
                    <option value="approved">Approved</option>
                    <option value="rejected">Rejected</option>
                  </select>
                </div>
                <div className="md:col-span-2 lg:col-span-3">
                  <Label>Bio / परिचय</Label>
                  <textarea 
                    name="bio" 
                    value={editFormData.bio} 
                    onChange={handleEditChange} 
                    rows={3}
                    className="w-full px-3 py-2 rounded-md border border-input bg-background text-sm resize-none"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
                <Button variant="outline" onClick={() => setEditingMember(null)}>
                  Cancel
                </Button>
                <Button onClick={saveMemberEdit} disabled={saving} className="bg-orange-600 hover:bg-orange-700">
                  {saving ? 'Saving...' : <><Save className="w-4 h-4 mr-2" /> Save Changes</>}
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default MemberManagement;
